import { motion } from "framer-motion";

function Stat({ value, suffix, suffixClass, label }) {
    return (
        <div className="flex flex-col items-center text-center border-t border-lpl-bg/25 pt-4">
            <p className="font-display font-extrabold text-3xl md:text-4xl text-lpl-bg leading-none">
                {value}
                <span className={suffixClass}>{suffix}</span>
            </p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-lpl-bg/75 leading-snug">
                {label}
            </p>
        </div>
    );
}

export default function Sylvie() {
    return (
        <section
            id="sylvie"
            className="relative py-24 md:py-32 bg-lpl-primary-deep text-lpl-bg overflow-hidden"
            data-testid="sylvie-section"
        >
            {/* Decorative big text */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-10 -right-10 font-serif italic text-[28vw] leading-none text-lpl-bg/[0.04] select-none"
            >
                Hello
            </div>

            <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                {/* Image side */}
                <motion.div
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-5 relative lg:sticky lg:top-28"
                >
                    <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-0">
                        {/* Offset yellow block */}
                        <div
                            aria-hidden
                            className="absolute -bottom-5 -right-5 w-full h-full bg-lpl-yellow"
                        />
                        <img
                            src="/images/sylvie.jpg"
                            alt="Sylvie devant la vitrine de La Petite Lunetterie"
                            className="relative w-full h-full object-cover"
                            data-testid="sylvie-photo"
                        />
                        {/* Lavender dot accent */}
                        <div className="absolute -top-6 -left-6 w-16 h-16 rounded-full bg-lpl-lavender hidden md:block" />
                    </div>

                    {/* Stats — alignés sur la largeur visuelle (photo + offset) */}
                    <div className="mt-14 max-w-[calc(28rem+1.25rem)] mx-auto lg:ml-0 grid grid-cols-3 gap-3">
                        <Stat
                            value="30"
                            suffix="+"
                            suffixClass="text-lpl-yellow"
                            label="ans d'expérience"
                        />
                        <Stat
                            value="40"
                            suffix="+"
                            suffixClass="text-lpl-lavender"
                            label="marques choisies"
                        />
                        <Stat
                            value="100"
                            suffix="%"
                            suffixClass="text-lpl-terracotta"
                            label="indépendante"
                        />
                    </div>
                </motion.div>

                {/* Text side */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="lg:col-span-7"
                >
                    {/* Block 1 — Mon histoire */}
                    <div className="relative">
                        <p className="font-serif italic text-xl text-lpl-yellow mb-3">
                            Mon histoire
                        </p>
                        <h2
                            className="font-display font-semibold text-4xl md:text-6xl leading-[0.95] tracking-tight mb-8"
                            data-testid="sylvie-title"
                        >
                            Moi c'est{" "}
                            <span className="font-serif italic font-normal text-lpl-yellow">
                                Sylvie
                            </span>
                            <span className="font-serif italic font-normal text-lpl-lavender">
                                .
                            </span>
                        </h2>
                        <div className="space-y-5 text-base md:text-lg leading-relaxed text-lpl-bg/85 max-w-xl">
                            <p>
                                Tout a commencé avec ma toute première paire de
                                lunettes. Un simple achat, et pourtant…{" "}
                                <span className="font-serif italic text-lpl-yellow not-italic">
                                    Une révélation
                                </span>{" "}
                                ! J'ai découvert un univers qui allie
                                esthétique, précision et relation humaine. C'est
                                ce jour-là que j'ai su : je voulais devenir
                                opticienne.
                            </p>
                            <p>
                                30 ans plus tard, ma passion est intacte. J'ai
                                fait le choix de l'indépendance pour exercer mon
                                métier avec liberté, authenticité… et beaucoup
                                d'enthousiasme !
                            </p>
                        </div>
                    </div>

                    {/* Separator */}
                    <div
                        aria-hidden
                        className="my-12 h-px w-32 bg-lpl-bg/30"
                    />

                    {/* Block 2 — La boutique */}
                    <div className="relative">
                        <p className="font-serif italic text-xl text-lpl-lavender mb-3">
                            La boutique
                        </p>
                        <h3 className="font-display font-semibold text-3xl md:text-4xl leading-[1] tracking-tight mb-6 max-w-lg">
                            Un lieu qui me{" "}
                            <span className="font-serif italic font-normal text-lpl-yellow">
                                ressemble
                            </span>
                            <span className="text-lpl-lavender">.</span>
                        </h3>
                        <div className="space-y-5 text-base md:text-lg leading-relaxed text-lpl-bg/85 max-w-xl">
                            <p>
                                En 2018, j'ai ouvert ma propre boutique à
                                Bourg-en-Bresse. Un lieu accueillant et
                                chaleureux, où chaque client est accompagné avec
                                attention et bienveillance.
                            </p>
                            <p>
                                Ici, rien n'est conventionnel. Chaque conseil,
                                chaque monture, chaque ajustement sont pensés
                                pour vous.
                            </p>
                            <p className="font-serif italic text-xl md:text-2xl text-lpl-yellow pt-2">
                                Au plaisir de vous rencontrer et de vous faire
                                découvrir mon univers !
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
